import { useState } from 'react';
import { Input, Spinner } from 'reactstrap';
import PropTypes from 'prop-types';
import * as Icon from 'react-feather';
import { Search, ChevronDown, ChevronUp } from 'react-feather';
import Table from '../ui/Table';
import ModalAviso from '../../layouts/modals/ModalAviso';
import alumnosApiService from '../../services/http/alumnosApiService';
import { getItem, setItem, STORAGE_KEYS } from '../../services/storage';
import colors from '../../theme/colors';

const SelectionButton = ({ user = {}, handleImpersonation }) => {
  const { isVerified } = user;

  return (
    <button
      type="button"
      onClick={() => handleImpersonation(user)}
      disabled={!isVerified}
      style={{
        display: 'inline-flex', alignItems: 'center', padding: '4px 8px',
        borderRadius: '6px', border: 'none', cursor: isVerified ? 'pointer' : 'not-allowed',
        backgroundColor: isVerified ? colors.primary : 'transparent',
        color: isVerified ? '#fff' : '#9ca3af',
        opacity: isVerified ? 1 : 0.5, transition: 'all 0.15s',
      }}
    >
      {isVerified ? <Icon.LogIn size={14} /> : <Icon.Slash size={14} />}
    </button>
  );
};

SelectionButton.propTypes = {
  user: PropTypes.shape({
    id: PropTypes.number,
    nombre: PropTypes.string,
    email: PropTypes.string,
    isVerified: PropTypes.bool,
  }),
  handleImpersonation: PropTypes.func,
};

const FormularioSuplantacionIdentidad = () => {
  const [showFilters, setShowFilters] = useState(true);
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [dataApi, setDataApi] = useState([]);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [searchUser, setSearchUser] = useState('');
  const [showModalConfirmation, setShowModalConfirmation] = useState(false);
  const [userSelected, setUserSelected] = useState(null);

  const handleLoadData = async (e) => {
    if (e) e.preventDefault();
    setIsDataLoading(true);

    try {
      const responseData = await alumnosApiService.buscarUsuarios({
        headquarter: 'CEDESCA',
        search: searchUser,
      });
      setDataApi(responseData);
      setDataLoaded(true);
    } catch (error) {
      setDataApi([]);
      setDataLoaded(false);
    } finally {
      setIsDataLoading(false);
    }
  };

  const handleImpersonation = (user) => {
    setUserSelected(user);
    setShowModalConfirmation(true);
  };

  const execImpersonation = () => {
    const currentUserSession = getItem(STORAGE_KEYS.USER) ?? {};
    const { profileObj, tokenId } = currentUserSession;
    const newUserSession = {
      adminProfileObj: profileObj,
      profileObj: userSelected,
      tokenId,
      impersonating: true,
    };
    setItem(STORAGE_KEYS.USER, newUserSession);
    window.location.href = '/';
  };

  const userColumns = [
    { Header: 'ID', accessor: 'id' },
    { Header: 'Nombre', accessor: 'nombre' },
    { Header: 'Email', accessor: 'email' },
    { Header: 'Acciones', Cell: ({ original }) => original.actions },
  ];

  return (
    <>
      <form onSubmit={handleLoadData}>
        <div style={{
          backgroundColor: '#fff', borderRadius: '12px',
          border: '1px solid #e5e7eb', boxShadow: '0 1px 3px rgb(0 0 0 / 0.06)',
          overflow: 'visible',
        }}>
          {/* Toolbar */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '12px 16px',
            borderBottom: showFilters ? '1px solid #f3f4f6' : 'none',
          }}>
            <button
              type="button"
              onClick={() => setShowFilters(!showFilters)}
              style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                background: 'none', border: 'none', cursor: 'pointer',
                fontSize: '0.8rem', fontWeight: 600, color: '#374151',
                padding: '4px 8px', borderRadius: '6px',
              }}
            >
              <Search size={15} style={{ color: '#9ca3af' }} />
              Filtros
              {showFilters
                ? <ChevronUp size={14} style={{ color: '#9ca3af' }} />
                : <ChevronDown size={14} style={{ color: '#9ca3af' }} />}
            </button>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                type="submit"
                disabled={isDataLoading}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '6px 14px',
                  fontSize: '0.75rem', fontWeight: 600, borderRadius: '6px', border: 'none',
                  backgroundColor: colors.primary, color: '#fff',
                  cursor: isDataLoading ? 'not-allowed' : 'pointer',
                  opacity: isDataLoading ? 0.5 : 1, transition: 'all 0.15s',
                }}
              >
                {isDataLoading ? <Spinner style={{ height: '13px', width: '13px' }} /> : <Search size={13} />}
                {isDataLoading ? 'Buscando...' : 'Buscar'}
              </button>
            </div>
          </div>

          {/* Filtros colapsables */}
          {showFilters && (
            <div style={{
              padding: '16px',
              opacity: isDataLoading ? 0.5 : 1,
              pointerEvents: isDataLoading ? 'none' : 'auto',
              transition: 'opacity 0.2s',
            }}>
              <div style={{ maxWidth: '400px' }}>
                <label htmlFor="searchsuplantacionidentidad" className="form-label">
                  Selecciona un usuario
                </label>
                <Input
                  type="text"
                  name="text"
                  id="searchsuplantacionidentidad"
                  placeholder="Busca un usuario"
                  value={searchUser}
                  onChange={(e) => setSearchUser(e.target.value)}
                />
              </div>
            </div>
          )}
        </div>
      </form>

      {dataLoaded && (
        <div style={{ marginTop: '16px' }}>
          <Table
            columns={userColumns}
            data={dataApi.map((user) => ({
              ...user,
              actions: <SelectionButton user={user} handleImpersonation={handleImpersonation} />,
            }))}
            defaultPageSize={10}
            showPaginationBottom
            className="-striped -highlight text-center"
          />
        </div>
      )}

      {showModalConfirmation && !!userSelected && (
        <ModalAviso
          toggle={execImpersonation}
          header={`Confirmar la suplantación a ${userSelected.email}`}
          onClose={() => setShowModalConfirmation(false)}
        />
      )}
    </>
  );
};

export default FormularioSuplantacionIdentidad;
